import type { AuthConfigNanoId } from '@composio/types'
import type { ColumnDef } from '@tanstack/react-table'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useRouter } from 'next/router'
import { css } from '@emotion/css'
import { useMutation } from '@tanstack/react-query'
import clsx from 'clsx'
import { DataTable } from 'design-system/ui/molecules/table'
import cloneDeep from 'lodash/cloneDeep'
import { CopyIcon, Trash2Icon } from 'lucide-react'

import type { GetAuthConfigsResponse } from '~/generated/client/types.gen'
import type { NanoId } from '~/types/nominal'
import { useApiKey } from '~/dataProcessor/hooks/user'
import { Button } from '~/design-system/atom/button'
import { Link } from '~/design-system/atom/link'
import { Switch } from '~/design-system/atom/switch'
import { useToast } from '~/design-system/atom/use-toast'
import {
  deleteAuthConfigsByNanoid,
  patchAuthConfigsByNanoidByStatus,
} from '~/generated/client'
import { QUERY_KEYS, useGetAllIntegrations } from '~/hooks/query/tools'
import { queryClient } from '~/queryClient'
import { ErrorBoundary } from '~/ui/components/base/errorBoundary'
import { IdDropDown } from '~/ui/components/base/idDropDown'
import { CodeWInstructionModal } from '~/ui/containers/codeModal'
import { DeletePopup } from '~/ui/containers/common/ActionButton'
import ProfileGravatar from '~/ui/containers/common/ProfileGravatar'
import { formatActionName, showFirstAndLastNLetters } from '~/utils/string'
import { CodeButton } from '../connector'

type ExtendedGetAuthConfigsResponse = GetAuthConfigsResponse & {
  items: (GetAuthConfigsResponse['items'][number] & {
    id: NanoId
  })[]
}

// Define a type for the integration item from the API response
type AuthConfigItem = GetAuthConfigsResponse['items'][0] & {
  id: NanoId
}

// Define a type for the cell props
interface CellProps {
  row: {
    original: AuthConfigItem
  }
}

// Cell renderer components
const ToolCell = React.memo(({ row }: CellProps) => {
  const connector = row.original
  return (
    <div className="flex items-center gap-3">
      <div
        className={clsx(
          ' h-5 w-5 ',
          css`
            background-image: url('${connector.toolkit.logo}');
            background-position: center;
            background-repeat: no-repeat;
            background-size: cover;
          `,
        )}
      ></div>{' '}
      <div className="text-[13px] font-[400] ">
        {formatActionName(connector.name || '')}
      </div>
    </div>
  )
})

const IntegrationIdCell = React.memo(({ row }: CellProps) => {
  const connector = row.original

  const { toast } = useToast()

  return (
    <div className="w-fit text-[12px] font-[400] text-black-600">
      <IdDropDown
        onCopy={() => {
          toast({
            title: 'Copied!',
            description: 'Integration ID copied to clipboard',
          })
        }}
        ids={[
          {
            key: 'v1/v2',
            label: 'v1/v2 (uuid)',
            id: connector.uuid,
          },
          {
            key: 'v3',
            label: 'v3 (nanoid)',
            id: connector.id,
          },
        ]}
      >
        <span className="flex cursor-pointer items-center gap-2">
          {showFirstAndLastNLetters(connector.id, 10)}
          <CopyIcon height={12} width={12} strokeWidth={1.4} />
        </span>
      </IdDropDown>
      {/* </ToolTipBox> */}
    </div>
  )
})

const ConnectedAccountsCell = React.memo(({ row }: CellProps) => {
  const connector = row.original
  return (
    <div className="text-center text-[12px] font-[500] text-black-600">
      {connector.no_of_connections}
    </div>
  )
})

const CreatedByCell = React.memo(({ row }: CellProps) => {
  const connector = row.original
  return (
    <div className="w-fit pl-6 text-[12px] font-[500] text-black-600">
      <ProfileGravatar
        direction={'bottom'}
        member={{
          name: connector.created_by ?? '',
          email: connector.created_by ?? '',
        }}
      />
    </div>
  )
})

const ActionsCell = React.memo(({ row }: CellProps) => {
  const connector = row.original
  const [isChecked, setIsChecked] = useState(connector.status === 'ENABLED')
  const { toast } = useToast()
  const router = useRouter()

  const handleStatusChange = useCallback(
    (enabled: boolean) => {
      setIsChecked(enabled)
      patchAuthConfigsByNanoidByStatus({
        nanoid: connector.id,
        status: enabled ? 'ENABLED' : 'DISABLED',
      })
        .then(() => {
          toast({
            title: 'Status updated',
            description: `Connector ${enabled ? 'enabled' : 'disabled'}`,
          })
        })
        .catch(() => {
          setIsChecked(!enabled)
          toast({
            title: 'Error',
            description: `Error in updating status`,
            variant: 'destructive',
          })
        })
    },
    [connector.id, toast],
  )

  return (
    <div
      className="flex items-center gap-3 pr-3"
      onClick={(e) => {
        e.stopPropagation()
      }}
    >
      <Switch
        checked={isChecked}
        id={`switch-${connector.id}`}
        onCheckedChange={(e) => {
          handleStatusChange(e)
        }}
      />
      <DeleteConnectorAction
        connectorId={connector.id}
        toolSlug={connector.toolkit.slug || ''}
      />
      <Button
        size={28}
        variant={'plain'}
        className=" mt-[auto] w-[fit-content]  rounded-[10px] border-[.5px] border-[#D5D5D5] !bg-[#FBFBFB] text-[1px] font-[400] tracking-[.3px] text-black-200"
        onClick={(e) => {
          e.preventDefault()
          e.stopPropagation()
          router.push(`/connector/${connector.id}`)
        }}
      >
        <div className="mt-[2px] text-[13px] font-[500]">Open</div>
      </Button>
    </div>
  )
})

export default function LiveToolsList({ appName }: { appName?: string }) {
  const router = useRouter()
  const page =
    Number.parseInt(router.query.page as string, 10) ||
    Number.parseInt(
      new URL(window.location.toString())?.searchParams?.get('page')!,
      10,
    ) ||
    1

  const {
    data: { items: connectors } = {},
    isLoading,
    isError,
    refetch,
  } = useGetAllIntegrations({
    toolkitSlug: appName,
    page,
  })

  useEffect(() => {
    refetch()
    // eslint-disable-next-line react-compiler/react-compiler
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page])

  // Define columns for DataTable
  const columns = useMemo<ColumnDef<AuthConfigItem, unknown>[]>(
    () => [
      {
        accessorKey: 'name',
        header: 'Tool',
        size: 300,
        cell: ({ row }) => <ToolCell row={row} />,
      },
      {
        accessorKey: 'id',
        header: 'Integration ID',
        size: 300,
        cell: ({ row }) => <IntegrationIdCell row={row} />,
      },
      {
        accessorKey: 'member',
        header: 'Created by',
        size: 150,
        cell: ({ row }) => <CreatedByCell row={row} />,
      },
      {
        id: 'actions',
        header: () => <div className="pr-4 text-right">Actions</div>,
        size: 220,
        cell: ({ row }) => <ActionsCell row={row} />,
      },
    ],
    [],
  )

  // Empty state component
  const emptyState = <div>{`No integrations setup yet`}</div>

  // Error component
  const errorComponent = (
    <div className="mt-10 text-center text-red-500">
      <p>Failed to fetch integration data.</p>
      <button
        onClick={() => void refetch()}
        className="mt-2 text-blue-500 underline"
      >
        Retry
      </button>
    </div>
  )

  return (
    <ErrorBoundary
      fallback={
        <div>{`Something went wrong. We'll work on getting this fixed.`}</div>
      }
    >
      <>
        <div className="mt-[0px] flex items-center">
          <div className="pt-2 text-[13px] font-[400] text-black-600">
            Integrations
          </div>
          <div className="ml-auto mt-2 flex items-center gap-2">
            <ShowIntegrationCode />
          </div>
        </div>
        <DataTable
          columns={columns}
          data={(connectors as ExtendedGetAuthConfigsResponse['items']) ?? []}
          isLoading={isLoading}
          isError={isError}
          emptyState={emptyState}
          errorComponent={errorComponent}
          onRowClick={(row: AuthConfigItem) => {
            router.push(`/connector/${row.id}`)
          }}
        />
      </>
    </ErrorBoundary>
  )
}

export const sampleCodeBlockData = {
  content: {
    python: [
      {
        type: 'blockWizard',
        content: {
          heading: 'Code for getting all apps',
          blocks: [
            {
              heading: 'Fetch integrations',
              text: `from composio import ComposioToolSet, App

toolset = ComposioToolSet(api_key={{api_key}})
integrations = toolset.get_integrations(##Insert your code here##)
print(integrations)
`,
              language: 'python',
            },
          ],
        },
      },
      {
        type: 'content',
        content: (
          <div className="mt-6 text-[15px] font-[400]">
            Visit{' '}
            <Link
              className="text-[15px]"
              href="https://docs.composio.dev/patterns/Auth/Integrations"
              isExternal={true}
            >
              docs
            </Link>{' '}
            to learn more about integrations
          </div>
        ),
      },
    ],
    javascript: [
      {
        type: 'blockWizard',
        content: {
          heading: 'Code for getting all apps',
          blocks: [
            {
              heading: 'Fetch integrations',
              text: `import {Composio} from "composio-core";
// get composio client
const composio = new Composio({apiKey: {{api_key}}});

const integrations = await composio.integrations.list({##Insert your code here##})
`,
              language: 'javascript',
            },
          ],
        },
      },
      {
        type: 'content',
        content: (
          <div className="mt-6 text-[15px] font-[400]">
            Visit{' '}
            <Link
              className="text-[15px]"
              href="https://docs.composio.dev/patterns/Auth/Integrations"
              isExternal={true}
            >
              docs
            </Link>{' '}
            to learn more about integrations
          </div>
        ),
      },
    ],
  },
}

const ShowIntegrationCode = () => {
  const [showCode, setShowCode] = useState(false)

  const apiKey = useApiKey()

  const { query } = useRouter()

  const appId = query.appId as string
  const transformedData = useMemo(() => {
    // transform data of sampleCodeBlockData
    const data = cloneDeep(sampleCodeBlockData)

    for (const language of Object.keys(data.content)) {
      // @ts-expect-error - this is okay for now
      for (const languageItem of data.content[language]) {
        //replace blocks with api key and integration id
        const { blocks } = languageItem.content

        if (!blocks) continue

        for (const block of blocks) {
          block.text = block.text.replace('{{api_key}}', `"${apiKey}"`)
          let insertContent = ``

          if (language === 'python') {
            if (appId) insertContent += `\n  app="${appId}",`
          } else {
            if (appId) insertContent += `\n  appName: "${appId}",`
          }

          block.text = block.text.replace(
            '##Insert your code here##',
            insertContent.length > 0 ? `${insertContent}\n` : '',
          )
        }
      }
    }

    return data
  }, [apiKey, appId])

  return (
    <div>
      <CodeButton setShowCode={setShowCode} />
      {showCode && (
        <CodeWInstructionModal
          title="Get integrations with Our SDKs"
          data={transformedData}
          setOpen={() => setShowCode(false)}
        />
      )}
    </div>
  )
}

export const DeleteConnectorAction = ({
  connectorId,
  toolSlug = '',
}: {
  connectorId: AuthConfigNanoId
  toolSlug: string
}) => {
  const [open, setOpen] = useState(false)
  const { refetch } = useGetAllIntegrations({
    toolkitSlug: undefined,
    page: 1,
  })
  const { toast } = useToast()

  const router = useRouter()

  const isConnectorPage = router.pathname.includes('connector')
  const handleDeletion = useMutation({
    mutationFn: () => deleteAuthConfigsByNanoid({ nanoid: connectorId }),
    onSuccess: () => {
      toast({
        title: 'Success',
        description: 'Integration was deleted successfully',
        variant: 'success',
      })

      if (isConnectorPage) {
        queryClient.invalidateQueries({
          queryKey: QUERY_KEYS.getIntegrationQueryKey(toolSlug),
        })
        router.push('/integrations')
      } else {
        setOpen(false)
        refetch()
      }
    },
    onError: (error: Error) => {
      toast({
        title: 'Error',
        description: error?.message,
        variant: 'destructive',
      })
    },
  })

  const handleDelete = () => {
    handleDeletion.mutate()
  }

  return (
    <>
      {open && (
        <DeletePopup
          handleDelete={handleDelete}
          setOpen={() => {
            setOpen(false)
          }}
        />
      )}
      <div
        onClick={() => {
          setOpen(true)
        }}
        className="cursor-pointer px-[px] text-black-600"
      >
        <Trash2Icon height={14} width={14} />
      </div>
    </>
  )
}
